import React, { useState, useEffect, useCallback } from 'react';
import { reimbursementAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CheckCircle, XCircle, Clock, IndianRupee, FileImage, Banknote, AlertCircle, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  submitted: { label: 'Submitted', cls: 'bg-yellow-100 text-yellow-700' },
  under_review: { label: 'Under Review', cls: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700' },
  partially_approved: { label: 'Partial', cls: 'bg-orange-100 text-orange-700' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
  paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-800' },
};

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

const ReimbursementReview = () => {
  const [claims, setClaims] = useState([]);
  const [summary, setSummary] = useState({ pending_count: 0, approved_unpaid_amount: 0 });
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [isPaidOpen, setIsPaidOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reviewForm, setReviewForm] = useState({ action: 'approve', approved_amount: '', admin_note: '' });
  const [paymentRef, setPaymentRef] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      const [claimRes, summaryRes] = await Promise.all([
        reimbursementAPI.getAll(params),
        reimbursementAPI.getSummary(),
      ]);
      setClaims(claimRes.data);
      setSummary(summaryRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { const iv = setInterval(fetchData, 30000); return () => clearInterval(iv); }, [fetchData]);

  const openReview = (claim, action) => {
    setSelectedClaim(claim);
    setReviewForm({ action, approved_amount: String(claim.amount_claimed), admin_note: '' });
    setIsReviewOpen(true);
  };

  const openPaid = (claim) => {
    setSelectedClaim(claim);
    setPaymentRef('');
    setIsPaidOpen(true);
  };

  const handleReview = async () => {
    if (reviewForm.action === 'partial_approve') {
      if (!reviewForm.approved_amount || parseFloat(reviewForm.approved_amount) <= 0) { alert('Enter a valid approved amount'); return; }
      if (parseFloat(reviewForm.approved_amount) >= selectedClaim.amount_claimed) { alert('Partial amount must be less than claimed amount'); return; }
      if (!reviewForm.admin_note.trim()) { alert('A note is required for partial approval'); return; }
    }
    if (reviewForm.action === 'reject' && !reviewForm.admin_note.trim()) { alert('Rejection reason is required'); return; }
    setSaving(true);
    try {
      const body = { action: reviewForm.action, admin_note: reviewForm.admin_note || null };
      if (reviewForm.action === 'partial_approve') body.approved_amount = parseFloat(reviewForm.approved_amount);
      await reimbursementAPI.review(selectedClaim.id, body);
      setIsReviewOpen(false); setSelectedClaim(null);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handlePaid = async () => {
    setSaving(true);
    try {
      await reimbursementAPI.markPaid(selectedClaim.id, { payment_reference: paymentRef || null });
      setIsPaidOpen(false); setSelectedClaim(null);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const renderBillThumbs = (urls) => {
    if (!urls || urls.length === 0) return null;
    return (
      <div className="flex gap-2 flex-wrap">
        {urls.map((url, i) => {
          const full = getFileUrl(url);
          const isPdf = url.endsWith('.pdf');
          return (
            <button
              key={i}
              onClick={() => setPreviewUrl(full)}
              className="block w-16 h-16 border rounded overflow-hidden hover:ring-2 ring-blue-400 shrink-0 cursor-pointer bg-slate-100"
              data-testid={`bill-thumb-${i}`}
            >
              {isPdf ? (
                <div className="w-full h-full bg-red-50 flex items-center justify-center"><FileImage className="w-5 h-5 text-red-400" /></div>
              ) : (
                <img src={full} alt="Bill" className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-300 text-[10px]">No preview</div>'; }} />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Group by employee + report for better admin view
  const grouped = {};
  claims.forEach(c => {
    const key = c.report_id || c.id;
    if (!grouped[key]) grouped[key] = { employee: c.employee_name, title: c.report_title || c.category, claims: [] };
    grouped[key].claims.push(c);
  });

  return (
    <div className="space-y-5" data-testid="reimbursement-review-page">
      <div>
        <h2 className="text-xl font-bold text-[#1D35A0]">Reimbursement Claims</h2>
        <p className="text-sm text-slate-500">Review and approve employee expense claims individually</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white rounded-lg border p-3 shadow-sm flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center"><Clock className="w-4 h-4 text-yellow-600" /></div>
          <div><p className="text-xl font-bold text-[#1D35A0]" data-testid="pending-count">{summary.pending_count}</p><p className="text-[10px] text-slate-500">Pending Review</p></div>
        </div>
        <div className="bg-white rounded-lg border p-3 shadow-sm flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center"><Banknote className="w-4 h-4 text-green-600" /></div>
          <div><p className="text-xl font-bold text-[#1D35A0]" data-testid="unpaid-amount">Rs. {(summary.approved_unpaid_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p><p className="text-[10px] text-slate-500">Approved (Unpaid)</p></div>
        </div>
      </div>

      <Select value={filterStatus} onValueChange={setFilterStatus}>
        <SelectTrigger className="w-56" data-testid="filter-status"><SelectValue placeholder="All" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="submitted">Submitted</SelectItem>
          <SelectItem value="approved">Approved</SelectItem>
          <SelectItem value="partially_approved">Partially Approved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
          <SelectItem value="paid">Paid</SelectItem>
        </SelectContent>
      </Select>

      {/* Claims grouped by report */}
      <div className="space-y-5">
        {loading ? (
          <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1D35A0]" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <div className="bg-white rounded-lg border p-6 text-center text-slate-400"><AlertCircle className="w-8 h-8 mx-auto mb-1.5" /><p>No claims found</p></div>
        ) : Object.entries(grouped).map(([key, group]) => (
          <div key={key} className="bg-white rounded-lg border shadow-sm overflow-hidden">
            {/* Report header */}
            <div className="px-5 py-3 border-b bg-slate-50 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-[#1D35A0]">{group.employee}</p>
                <p className="text-xs text-slate-500">{group.title} — {group.claims.length} expense(s)</p>
              </div>
              <p className="text-sm font-bold text-[#1D35A0]">
                Total: Rs. {group.claims.reduce((s, c) => s + c.amount_claimed, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              </p>
            </div>

            {/* Individual claims */}
            <div className="divide-y">
              {group.claims.map(claim => {
                const st = STATUS_MAP[claim.status] || { label: claim.status, cls: 'bg-slate-100' };
                const isPending = ['submitted', 'under_review'].includes(claim.status);
                const isApproved = ['approved', 'partially_approved'].includes(claim.status);
                return (
                  <div key={claim.id} className="px-4 py-2.5 flex items-center gap-3" data-testid={`claim-row-${claim.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[#1D35A0]">{claim.description}</span>
                        <span className="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700">{claim.category}</span>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${st.cls}`}>{st.label}</span>
                        <span className="text-[10px] text-slate-400">{claim.date_incurred}</span>
                        {claim.admin_note && <span className="text-[10px] text-slate-500 italic">"{claim.admin_note}"</span>}
                      </div>
                      {claim.bill_urls?.length > 0 && (
                        <div className="mt-1">{renderBillThumbs(claim.bill_urls)}</div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#1D35A0]">Rs. {claim.amount_claimed?.toFixed(2)}</p>
                      {claim.approved_amount != null && claim.status !== 'rejected' && (
                        <p className={`text-xs font-semibold ${claim.approved_amount < claim.amount_claimed ? 'text-orange-600' : 'text-green-600'}`}>
                          Appr: Rs. {claim.approved_amount?.toFixed(2)}
                        </p>
                      )}
                      {claim.payment_reference && <p className="text-[10px] text-slate-400">Ref: {claim.payment_reference}</p>}
                    </div>
                    {(isPending || isApproved) && (
                      <div className="flex items-center gap-1.5 shrink-0">
                        {isPending && (
                          <>
                            <Button size="sm" onClick={() => openReview(claim, 'approve')} className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-2.5" data-testid={`approve-btn-${claim.id}`}>
                              <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openReview(claim, 'partial_approve')} className="text-orange-600 border-orange-200 hover:bg-orange-50 text-xs h-7 px-2" data-testid={`partial-btn-${claim.id}`}>
                              <IndianRupee className="w-3.5 h-3.5 mr-1" /> Partial
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => openReview(claim, 'reject')} className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7 px-2" data-testid={`reject-btn-${claim.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          </>
                        )}
                        {isApproved && (
                          <Button size="sm" onClick={() => openPaid(claim)} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-2.5" data-testid={`pay-btn-${claim.id}`}>
                            <Banknote className="w-3.5 h-3.5 mr-1" /> Paid
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Review Dialog */}
      <Dialog open={isReviewOpen} onOpenChange={v => { setIsReviewOpen(v); if (!v) setSelectedClaim(null); }}>
        <DialogContent className="max-w-md" data-testid="review-claim-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">
              {reviewForm.action === 'approve' ? 'Approve Claim' : reviewForm.action === 'partial_approve' ? 'Partial Approval' : 'Reject Claim'}
            </DialogTitle>
            <DialogDescription>
              {selectedClaim?.employee_name} — {selectedClaim?.description}
            </DialogDescription>
          </DialogHeader>

          {selectedClaim && (
            <div className="space-y-4 mt-4">
              {/* Show bill in review */}
              {selectedClaim.bill_urls?.length > 0 && (
                <div>
                  <p className="text-xs text-slate-500 mb-2">Receipts</p>
                  <div className="flex gap-2 flex-wrap">
                    {selectedClaim.bill_urls.map((url, i) => {
                      const full = getFileUrl(url);
                      return (
                        <button key={i} onClick={() => setPreviewUrl(full)} className="block w-20 h-20 border rounded overflow-hidden hover:ring-2 ring-blue-400 cursor-pointer bg-slate-100">
                          {url.endsWith('.pdf') ? (
                            <div className="w-full h-full bg-red-50 flex items-center justify-center"><FileImage className="w-4 h-4 text-red-400" /></div>
                          ) : (
                            <img src={full} alt="Bill" className="w-full h-full object-cover" onError={e => { e.target.onerror = null; e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-slate-300 text-xs">No preview</div>'; }} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="bg-slate-50 rounded-lg p-3 text-center">
                <p className="text-xs text-slate-500">Amount Claimed</p>
                <p className="text-xl font-bold text-[#1D35A0]">Rs. {selectedClaim.amount_claimed?.toFixed(2)}</p>
              </div>

              {reviewForm.action === 'approve' && (
                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Textarea placeholder="Optional note..." value={reviewForm.admin_note} onChange={e => setReviewForm(p => ({ ...p, admin_note: e.target.value }))} data-testid="review-note" />
                </div>
              )}

              {reviewForm.action === 'partial_approve' && (
                <div className="space-y-3 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="space-y-2">
                    <Label className="text-orange-800 font-semibold">Approved Amount (Rs.) *</Label>
                    <Input
                      type="text" inputMode="decimal"
                      placeholder="0.00"
                      value={reviewForm.approved_amount}
                      onChange={e => {
                        const v = e.target.value.replace(/[^0-9.]/g, '');
                        setReviewForm(p => ({ ...p, approved_amount: v }));
                      }}
                      className="text-lg font-bold"
                      data-testid="approved-amount"
                    />
                    <p className="text-sm text-orange-700 font-medium">
                      Difference: Rs. {((selectedClaim?.amount_claimed || 0) - (parseFloat(reviewForm.approved_amount) || 0)).toFixed(2)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-orange-800 font-semibold">Reason for partial approval *</Label>
                    <Textarea
                      placeholder="Explain why the full amount is not approved..."
                      value={reviewForm.admin_note}
                      onChange={e => setReviewForm(p => ({ ...p, admin_note: e.target.value }))}
                      rows={3} data-testid="review-note"
                    />
                  </div>
                </div>
              )}

              {reviewForm.action === 'reject' && (
                <div className="space-y-2 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <Label className="text-red-800 font-semibold">Rejection Reason *</Label>
                  <Textarea placeholder="Explain why..." value={reviewForm.admin_note} onChange={e => setReviewForm(p => ({ ...p, admin_note: e.target.value }))} rows={3} data-testid="review-note" />
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setIsReviewOpen(false)}>Cancel</Button>
                <Button
                  size="default" onClick={handleReview} disabled={saving}
                  className={`px-8 h-9 text-white ${
                    reviewForm.action === 'reject' ? 'bg-red-600 hover:bg-red-700' :
                    reviewForm.action === 'partial_approve' ? 'bg-orange-600 hover:bg-orange-700' :
                    'bg-green-600 hover:bg-green-700'
                  }`}
                  data-testid="submit-review-btn"
                >
                  {saving ? 'Processing...' : reviewForm.action === 'approve' ? 'Approve Full Amount' : reviewForm.action === 'partial_approve' ? 'Confirm Partial Approval' : 'Reject Claim'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={isPaidOpen} onOpenChange={v => { setIsPaidOpen(v); if (!v) setSelectedClaim(null); }}>
        <DialogContent className="max-w-md" data-testid="mark-paid-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#1D35A0]">Mark as Paid</DialogTitle>
            <DialogDescription>{selectedClaim?.employee_name} — Rs. {(selectedClaim?.approved_amount || selectedClaim?.amount_claimed)?.toFixed(2)}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Payment Reference (optional)</Label>
              <Input placeholder="e.g. NEFT/IMPS reference" value={paymentRef} onChange={e => setPaymentRef(e.target.value)} data-testid="payment-ref" />
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setIsPaidOpen(false)}>Cancel</Button>
              <Button size="default" onClick={handlePaid} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 h-9" data-testid="confirm-paid-btn">
                {saving ? 'Processing...' : 'Confirm Payment'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewUrl(null)} data-testid="image-preview-overlay">
          <button onClick={() => setPreviewUrl(null)} className="absolute top-4 right-4 text-white hover:text-slate-300 z-10">
            <X className="w-8 h-8" />
          </button>
          <div className="max-w-3xl max-h-[85vh] relative" onClick={e => e.stopPropagation()}>
            {previewUrl.endsWith('.pdf') ? (
              <iframe src={previewUrl} className="w-[700px] h-[80vh] bg-white rounded-lg" title="PDF Preview" />
            ) : (
              <img
                src={previewUrl}
                alt="Bill Preview"
                className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
                onError={e => { e.target.onerror = null; e.target.src = ''; e.target.alt = 'Image not available'; }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReimbursementReview;
